import React, { useState } from 'react';
import { Bell, Calendar, StickyNote, RefreshCw, Paperclip, SkipForward } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { formatDate } from '../../utils/dateUtils';
import { useAdvanceReminder } from '../../hooks/useReminders';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const statusStyles = {
    overdue: 'bg-status-overdue-bg text-status-overdue',
    active: 'bg-status-paid-bg text-status-paid',
    inactive: 'bg-surface-overlay text-text-muted',
};

const Row = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-surface-border last:border-0">
        <div className="flex items-center gap-1.5 w-32 shrink-0 text-text-muted">
            {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
            <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
        </div>
        <div className="flex-1 text-sm text-text-primary min-w-0">{children}</div>
    </div>
);

const ViewReminderModal = ({ isOpen, onClose, reminder, status }) => {
    const [isAdvancing, setIsAdvancing] = useState(false);
    const [advanceError, setAdvanceError] = useState('');
    const advanceReminder = useAdvanceReminder();
    const { t } = useLanguage();

    const handleAdvance = async () => {
        setAdvanceError('');
        setIsAdvancing(true);
        try {
            await advanceReminder.mutateAsync({ reminderUid: reminder.uid });
            onClose();
        } catch (error) {
            const msg = error.errors?.reminder ?? error.message ?? t(KEYS.REMINDER_VIEW_ADVANCE_ERROR);
            setAdvanceError(msg);
        } finally {
            setIsAdvancing(false);
        }
    };

    if (!reminder) return null;

    const canAdvance = reminder.is_active;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={reminder.title}
            subtitle="Reminder details"
            size="medium"
            footer={
                <div className="flex items-center justify-between w-full">
                    <div>
                        {canAdvance && (
                            <Button
                                variant="outline"
                                size="medium"
                                onClick={handleAdvance}
                                disabled={isAdvancing}
                                className="gap-2"
                            >
                                <SkipForward className="w-4 h-4" />
                                {isAdvancing ? t(KEYS.REMINDER_VIEW_ADVANCING) : t(KEYS.REMINDER_VIEW_ADVANCE)}
                            </Button>
                        )}
                    </div>
                    <Button variant="outline" size="medium" onClick={onClose}>
                        {t(KEYS.COMMON_CLOSE)}
                    </Button>
                </div>
            }
        >
            {advanceError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {advanceError}
                </div>
            )}

            <div className="divide-y divide-surface-border">
                {/* Status badge */}
                {status && (
                    <div className="pb-3 flex items-center gap-2">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${statusStyles[status]}`}>
                            {status}
                        </span>
                    </div>
                )}

                <Row icon={Bell} label="Title">
                    {reminder.title}
                </Row>

                <Row icon={Calendar} label="Due Date">
                    <span className="font-mono text-xs">{formatDate(reminder.due_date)}</span>
                </Row>

                <Row icon={RefreshCw} label="Frequency">
                    <span className="capitalize">{reminder.frequency}</span>
                </Row>

                <Row icon={StickyNote} label="Description">
                    {reminder.description
                        ? <span className="whitespace-pre-wrap text-xs">{reminder.description}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={Paperclip} label="Files">
                    {reminder.files && reminder.files.length > 0
                        ? (
                            <span className="text-xs text-text-muted">
                                {reminder.files.length} file{reminder.files.length !== 1 ? 's' : ''} attached
                            </span>
                        )
                        : <span className="text-text-muted">—</span>}
                </Row>
            </div>
        </Modal>
    );
};

export default ViewReminderModal;
